import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import { siteStats } from '@/mocks/homeContent';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';

const VALUES = [
  {
    icon: 'ri-focus-3-line',
    title: '专注日韩影视',
    description: '只做日韩电影、电视剧与综艺的中文字幕信息整理，垂直且清晰。',
  },
  {
    icon: 'ri-speed-up-line',
    title: '更新及时',
    description: '紧跟作品上线与字幕组进度，尽量第一时间同步字幕状态与版本信息。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '规范合规',
    description: '仅提供字幕信息展示与索引，尊重版权，不提供任何影视或字幕文件下载。',
  },
];

const TEAM = [
  { role: '内容整理组', duty: '负责作品资料、字幕版本信息的收集与校对。' },
  { role: '技术维护组', duty: '负责站点功能开发、检索与筛选体验的持续优化。' },
  { role: '社区联络组', duty: '对接合作字幕组，跟进投稿与内容反馈。' },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 日韩中文字幕',
    '了解日韩中文字幕网站的定位、发展历程、团队构成、版权声明与免责声明。'
  );

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className={`${CONTAINER} py-8 md:py-12`}>
          <Breadcrumb items={[{ label: '主页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            关于日韩中文字幕
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们是一个专注日韩影视中文字幕信息整理与分享的内容平台，希望让每一位中文观众都能更轻松地找到想看的作品字幕资料。
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} py-12 md:py-16`}>
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-14">
          <div className="overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20illustration%20of%20a%20collaborative%20subtitle%20editing%20workspace%20with%20screens%2C%20timeline%20graphs%20and%20floating%20text%20lines%2C%20deep%20indigo%20and%20warm%20amber%20gradient%2C%20soft%20ambient%20light%2C%20no%20readable%20text%2C%20elegant%20editorial%20composition%2C%20high%20detail%2C%20premium%20aesthetic&width=900&height=700&seq=about-team-01&orientation=landscape"
              alt="日韩中文字幕团队工作场景示意图"
              title="日韩中文字幕 - 团队与合作字幕组"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">网站定位与发展</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              站点起步于一群热爱日韩影视的观众自发整理的字幕资料库。随着作品数量不断增加，我们逐步建立起统一的资料结构，把作品信息、字幕版本、制作组与更新时间汇总到一处，方便大家查找与对比。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              发展至今，这里已经成为收录电影、电视剧与综艺三大类的字幕信息索引站。我们不生产字幕，也不提供下载，而是持续做一件简单的事：把散落的字幕信息整理清楚，让查找这件事变得省心。
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className={CONTAINER}>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们坚持的原则</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {VALUES.map((value) => (
              <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${value.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${CONTAINER} py-12 md:py-16`}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.4fr] lg:gap-14">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">团队简况</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              团队由志愿者组成，成员分布在不同城市，因为对日韩影视的共同兴趣走到一起。我们保持小而稳定的协作方式，欢迎志同道合的朋友加入。
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们
              <i className="ri-mail-send-line"></i>
            </Link>
          </div>
          <div className="space-y-4">
            {TEAM.map((item) => (
              <div key={item.role} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-team-line text-lg"></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.role}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.duty}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-950 py-12 md:py-16">
        <div className={CONTAINER}>
          <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">版权与免责声明</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-primary-800 bg-primary-900/50 p-6 text-sm leading-relaxed text-background-200">
              <p className="flex items-center gap-2 font-semibold text-background-50">
                <i className="ri-copyright-line text-accent-400"></i>
                版权声明
              </p>
              <p className="mt-3">
                本站页面内容、栏目结构及原创介绍文字均为本站整理，版权归本站所有。影视作品名称、海报、剧照等素材的著作权归原权利人所有，本站仅用于信息展示与介绍。
              </p>
            </div>
            <div className="rounded-lg border border-primary-800 bg-primary-900/50 p-6 text-sm leading-relaxed text-background-200">
              <p className="flex items-center gap-2 font-semibold text-background-50">
                <i className="ri-shield-line text-accent-400"></i>
                免责声明
              </p>
              <p className="mt-3">
                本站仅提供日韩影视中文字幕的信息展示与索引服务，<strong className="text-background-50">不提供影视文件与字幕文件下载</strong>
                。所有内容仅供学习与交流参考，请支持正版，如涉及版权问题请通过联系页面告知我们，我们将及时处理。
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}